/* eslint-disable @typescript-eslint/no-empty-function */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IError } from '../../../../../base/api';
import { UploadedFile } from '../../../../../base/components/Forms/FileUpload';
import { Rate } from '../../../../../base/enums';
import { Area, City } from '../../../../../base/interfaces/config';
import { EmployerData, RecommendationsWithProfiles, Responses, Speciality, UpgradePackage } from '../../interfaces';

export interface VacancyData {
    id: string;
    name: string;
    suitableForMen: boolean;
    suitableForWomen: boolean;
    suitableForCouple: boolean;
    suitableForStudents: boolean;
    rateType: Rate;
    rateFrom: string;
    rateTo: string;
    hoursPerMonthFrom: string;
    hoursPerMonthTo: string;
    currencyId: number;
    isHotVacancy: boolean;
    isExperienceNeeded: boolean;
    isPassportOnly: boolean;
    isAccomodationProvided: boolean;
    isFoodProvided: boolean;
    isTransferProvided: boolean;
    isClothesProvided: boolean;
    isSeasonalWork: boolean;
    isRemoteWork: boolean;
    isQuickApplyAvailable: boolean;
    city: City | null;
    description: string;
    images: UploadedFile[];
    requiredLanguageId: number | null;
    specialities: Speciality[];
    areas: Area[];
    workersCount: string;
    updatedAt: Date | null;
    views: number | null;
    isActive: boolean;
    isDraft: boolean;
}

export interface VacancyState {
    initialized: boolean;
    upgradePackages: UpgradePackage[];
    vacancyData: VacancyData | null;
    employer: EmployerData | null;
    recommendations: RecommendationsWithProfiles | null;
    responses: Responses | null;
    workerInvitationErrors: IError | null;
}

const initialState: VacancyState = {
    initialized: false,
    upgradePackages: [],
    vacancyData: null,
    employer: null,
    recommendations: null,
    responses: null,
    workerInvitationErrors: null,
};

export const vacancySlice = createSlice({
    name: 'employer__vacancy',
    initialState,
    reducers: {
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        init: () => {},
        initSuccess: (state) => {
            state.initialized = true;
        },
        getVacancyUpgradePackagesSuccess: (state, action: PayloadAction<UpgradePackage[]>) => {
            state.upgradePackages = action.payload;
        },
        getVacancyDataRequest: {
            reducer: () => {},
            prepare: (vacancyId?: string) => ({ payload: vacancyId }),
        },
        getVacancyDataSuccess: (state, action: PayloadAction<VacancyData>) => {
            state.vacancyData = action.payload;
        },
        setEmployerData: (state, action: PayloadAction<EmployerData>) => {
            state.employer = action.payload;
        },
        setRecommendations: (state, action: PayloadAction<RecommendationsWithProfiles | null>) => {
            state.recommendations = action.payload;
        },
        setResponses: (state, action: PayloadAction<Responses>) => {
            state.responses = action.payload;
        },
        closeVacancyRequest: {
            reducer: () => {},
            prepare: (id: string) => ({ payload: id }),
        },
        openVacancyRequest: {
            reducer: () => {},
            prepare: (id: string) => ({ payload: id }),
        },
        openVacancySuccess: (state) => {
            if (state.vacancyData) {
                state.vacancyData = {
                    ...state.vacancyData,
                    isActive: true,
                };
            }
        },
        renewVacancyRequest: {
            reducer: () => {},
            prepare: (id: string) => ({ payload: id }),
        },
        renewVacancySuccess: (state, action: PayloadAction<Date>) => {
            if (state.vacancyData) {
                state.vacancyData = {
                    ...state.vacancyData,
                    updatedAt: action.payload,
                };
            }
        },
        getRecommendationsRequest: {
            reducer: () => {},
            prepare: (id: string) => ({ payload: id }),
        },
        getRecommendationsSuccess: (state, action: PayloadAction<RecommendationsWithProfiles>) => {
            state.recommendations = action.payload;
        },
        recommendedWorkerFavoritesToggleRequest: {
            reducer: () => {},
            prepare: (id: number, value: boolean) => ({ payload: { id, value } }),
        },
        recommendedWorkerFavoritesToggleSuccess: (
            state,
            action: PayloadAction<{ id: number; value: boolean; type: 'workers' | 'recruiters' }>,
        ) => {
            if (state.recommendations) {
                state.recommendations[action.payload.type].profiles = state.recommendations[
                    action.payload.type
                ].profiles.map((i) => {
                    if (i.id === action.payload.id) {
                        return {
                            ...i,
                            isFavorite: action.payload.value,
                        };
                    }
                    return i;
                });
            }
        },
        inviteWorkerForVacancy: {
            reducer: () => {},
            prepare: (vacancyId: number, workerId: number) => ({ payload: { vacancyId, workerId } }),
        },
        inviteWorkerForVacancySuccess: {
            reducer: () => {},
            prepare: (vacancyId: number, workerId: number) => ({ payload: { vacancyId, workerId } }),
        },
        setWorkerInvitationErrors: (state, action: PayloadAction<IError | null>) => {
            state.workerInvitationErrors = action.payload;
        },
    },
});

export const vacancyActions = vacancySlice.actions;
export const vacancyReducer = vacancySlice.reducer;
